import { useState, useContext } from 'react'
import { AdminContext } from 'context/AdminContext'
import { DocumentDuplicateIcon } from '@heroicons/react/24/outline'
import placeholderLogo from 'media/placeholder_kinesis_logo.png'
import moment from 'moment'

import UserDirectoryUserInfoTableHeader from 'components/UserDirectory/UserDirectoryUserInfoTableHeader'
import UserDirectoryUserInfoRow from 'components/UserDirectory/UserDirectoryUserInfoRow'

const accountTypeNames = {
    'trainee': 'Athlete',
    'trainer': 'Coach',
    'organization-admin': 'Organization Admin',
    'organization-owner': 'Organization Owner',
    'super-admin': 'Super Admin'
}

export default function UserDirectoryTable() {

    const { UserDirectorySubContext } = useContext(AdminContext)

    const [showCopyToast, setShowCopyToast] = useState(false)
    const [copyToastText, setCopyToastText] = useState('')

    const OnEmailCopyButtonClicked = (email) => {
        navigator.clipboard.writeText(email);
        setCopyToastText(email);
        setShowCopyToast(true);
        setTimeout(() => setShowCopyToast(false), 2000);
    }

    const IsUserDirectoryUserSelected = (userId) => {
        return UserDirectorySubContext.selectedUserDirectoryUsers.some((selectedUser) => selectedUser.id === userId)
    }

    if (UserDirectorySubContext.userDirectory.length === 0) {
        return <div className="p-4 text-gray-400 text-center">No users found</div>
    }

    if (UserDirectorySubContext.selectedTabKey === 'user-info') {
        return (
            <>
                {showCopyToast && (
                    <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-2 rounded-md shadow-lg z-50 animate-fade-in-out">
                        Copied "{copyToastText}" to clipboard
                    </div>
                )}

                <div className="text-sm border-x border-t border-gray-400 divide-y divide-gray-400 rounded overflow-hidden bg-white w-full min-w-0">
                    <div className="overflow-x-auto w-full min-w-0">
                        <UserDirectoryUserInfoTableHeader />

                        {UserDirectorySubContext.userDirectory.map((user, index) => (
                            <UserDirectoryUserInfoRow
                                key={index}
                                user={user}
                                isSelected={IsUserDirectoryUserSelected(user.id)}
                                OnEmailCopyButtonClicked={OnEmailCopyButtonClicked}
                                UserDirectorySubContext={UserDirectorySubContext}
                            />
                        ))}
                    </div>
                </div>
            </>
        )
    }

    if (UserDirectorySubContext.selectedTabKey === 'user-info-legacy') {
        return (
            <>
                {showCopyToast && (
                    <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-2 rounded-md shadow-lg z-50 animate-fade-in-out">
                        Copied "{copyToastText}" to clipboard
                    </div>
                )}
    
                <div className="text-sm border-x border-t border-gray-400 divide-y divide-gray-400 rounded overflow-hidden bg-white">
                    <div className="overflow-x-auto">
                        <div className='grid grid-cols-44 bg-gray-200 font-semibold divide-x divide-gray-400 text-xs border-b border-gray-400' style={{minWidth: '1400px'}}>
                            <div className='col-span-2 px-2 py-1 text-center truncate'>Id</div>
                            <div className='col-span-5 px-2 py-1 truncate'>Organization</div>
                            <div className='col-span-2 px-2 py-1 truncate'>Type</div>
                            <div className='col-span-2 px-2 py-1 truncate'>First Name</div>
                            <div className='col-span-2 px-2 py-1 truncate'>Last Name</div>
                            <div className='col-span-6 px-2 py-1 truncate'>Email</div>
                            <div className='col-span-4 px-2 py-1 truncate'>Created Date</div>
                            <div className='col-span-3 px-2 py-1 text-center truncate'>Personal Info</div>
                            <div className='col-span-3 px-2 py-1 text-center truncate'>Wearable</div>
                            <div className='col-span-3 px-2 py-1 text-center truncate'>Assessment</div>
                            <div className='col-span-3 px-2 py-1 text-center truncate'>Fitness Profile</div>
                            <div className='col-span-3 px-2 py-1 text-center truncate'>Event Schedule</div>
                            <div className='col-span-3 px-2 py-1 text-center truncate'>Membership</div>
                            <div className='col-span-3 px-2 py-1 text-center truncate'>First Workout</div>
                        </div>
    
                        {UserDirectorySubContext.userDirectory.map((user) => (
                            <div 
                                key={user.id} 
                                className="grid grid-cols-44 cursor-pointer hover:bg-gray-100 divide-x divide-gray-400 border-b border-gray-400"
                                style={{minWidth: '1400px'}}
                                onClick={() => UserDirectorySubContext.OnUserDirectoryRowPressed(user)} 
                            >
                                <div className='col-span-2 px-2 py-1 text-center truncate'>{user.id}</div>
    
                                {user.organizationName !== null && 
                                    <div className='col-span-5 px-2 py-1 truncate flex items-center gap-x-1'>
                                        <img src={user.organizationLogo || placeholderLogo} alt="Organization Logo" className="w-3 h-3 rounded-full border border-gray-200" />
                                        <div className="truncate">{user.organizationName}</div>
                                    </div>
                                }
                                {user.organizationName === null && 
                                    <div className='col-span-5 px-2 py-1 truncate text-gray-400'>Individual</div>
                                }
    
                                <div className='col-span-2 px-2 py-1 truncate'>{accountTypeNames[user.type]}</div>

                                {user?.firstName?.length > 0 ? (
                                    <div className='col-span-2 px-2 py-1 truncate'>{user.firstName}</div>
                                ) : (
                                    <div className='col-span-2 px-2 py-1 truncate text-gray-400'>None</div>
                                )}

                                {user?.lastName?.length > 0 ? (
                                    <div className='col-span-2 px-2 py-1 truncate'>{user.lastName}</div>
                                ) : (
                                    <div className='col-span-2 px-2 py-1 truncate text-gray-400'>None</div>
                                )}
    
                                {user.email !== '' && 
                                    <div className='col-span-6 pl-2 pr-1 py-1 truncate flex items-center justify-between gap-x-2'>
                                        <div className="truncate flex-grow" title={user.email}>{user.email}</div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                OnEmailCopyButtonClicked(user.email);
                                            }}
                                            className="pr-0.5 hover:bg-gray-100 rounded-full flex-shrink-0"
                                            title="Copy email"
                                        >
                                            <DocumentDuplicateIcon className="h-4 w-4 text-gray-500" />
                                        </button>
                                    </div>
                                }
                                {user.email === '' && <div className='col-span-6 px-2 py-1 truncate text-gray-400'>None</div>}
    
                                <div className='col-span-4 px-2 py-1 truncate' title={`${moment.utc(user.createdDate).local().format('MMMM DD, YYYY')} ${moment.utc(user.createdDate).local().format('hh:mm A')}`}>
                                    {moment.utc(user.createdDate).local().format('MM/DD/YY hh:mm A')}
                                </div>
                                
                                {user.type === 'trainee' && 
                                    <>
                                        <div className={`col-span-3 px-2 py-1 truncate text-center font-bold ${user.personalInfoCompleted ? 'bg-green-100 text-green-500' : 'bg-red-100 text-red-500'}`}>
                                            {user.personalInfoCompleted ? 'Y' : 'N'}
                                        </div>
    
                                        <div className={`col-span-3 px-2 py-1 truncate text-center font-bold ${user.terraUserId && user.terraUserId.length > 0 ? 'bg-green-100 text-green-500' : 'bg-red-100 text-red-500'}`}>
                                            {user.terraUserId && user.terraUserId.length > 0 ? 'Y' : 'N'}
                                        </div>
    
                                        <div className={`col-span-3 px-2 py-1 truncate text-center font-bold ${user.assessmentCompleted ? 'bg-green-100 text-green-500' : 'bg-red-100 text-red-500'}`}>
                                            {user.assessmentCompleted ? 'Y' : 'N'}
                                        </div>
    
                                        <div className={`col-span-3 px-2 py-1 truncate text-center font-bold ${user.fitnessProfileCompleted ? 'bg-green-100 text-green-500' : 'bg-red-100 text-red-500'}`}>
                                            {user.fitnessProfileCompleted ? 'Y' : 'N'}
                                        </div>
    
                                        <div className={`col-span-3 px-2 py-1 truncate text-center font-bold ${user.eventScheduleCompleted ? 'bg-green-100 text-green-500' : 'bg-red-100 text-red-500'}`}>
                                            {user.eventScheduleCompleted ? 'Y' : 'N'}
                                        </div>
    
                                        <div className={`col-span-3 px-2 py-1 truncate text-center font-bold ${user.membershipActivationCompleted ? 'bg-green-100 text-green-500' : 'bg-red-100 text-red-500'}`}>
                                            {user.membershipActivationCompleted ? 'Y' : 'N'}
                                        </div>
    
                                        <div className={`col-span-3 px-2 py-1 truncate text-center font-bold ${user.firstWorkoutCompleted ? 'bg-green-100 text-green-500' : 'bg-red-100 text-red-500'}`}>
                                            {user.firstWorkoutCompleted ? 'Y' : 'N'}
                                        </div>
                                    </>
                                }
    
                                {(user.type === 'trainer' || user.type === 'organization-admin' || user.type === 'organization-owner' || user.type === 'super-admin') && 
                                    <>
                                        <div className='col-span-3 px-2 py-1 truncate text-center font-bold bg-gray-200 text-gray-400'>N/A</div>
                                        <div className='col-span-3 px-2 py-1 truncate text-center font-bold bg-gray-200 text-gray-400'>N/A</div>
                                        <div className='col-span-3 px-2 py-1 truncate text-center font-bold bg-gray-200 text-gray-400'>N/A</div>
                                        <div className='col-span-3 px-2 py-1 truncate text-center font-bold bg-gray-200 text-gray-400'>N/A</div>
                                        <div className='col-span-3 px-2 py-1 truncate text-center font-bold bg-gray-200 text-gray-400'>N/A</div>
                                        <div className='col-span-3 px-2 py-1 truncate text-center font-bold bg-gray-200 text-gray-400'>N/A</div>
                                        <div className='col-span-3 px-2 py-1 truncate text-center font-bold bg-gray-200 text-gray-400'>N/A</div>
                                    </>
                                }
    
                            </div>
                        ))}
                    </div>
                </div>
            </>
        )
    }
}
